import { TransientError } from '../types/errors';
import { GenericNetworkServiceInfo, HTTPRequestInfo, HTTPResponseInfo } from '../types/obs';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { promisefy } from '../utils/promisefy';
/**
 * OBS.Live HTTP listener service API wrapper.
 *
 * Provides basic HTTP request routing and handling functionality.
 *
 * Note: OBS.Live HTTP listeners run in the scope of the current
 *       browser widget. When the browser closes, the HTTP listener
 *       is destroyed.
 *
 * Basic usage:
 *
 *    const httpServer = new HttpListenerService({});
 *
 *    httpServer.get('/login', (request, response) => {
 *      const token = request.query.token;
 *
 *      if (token) {
 *        response.send({ success: true });
 *      } else {
 *        response.send({ success: false });
 *      }
 *    });
 *
 *    httpServer.get('/error', (request, response) => {
 *      response.redirect('https://www.streamelements.com/error');
 *    });
 *
 *    httpServer.start().then((listenerProps) => {
 *      console.log('HTTP server started on port ', listenerProps.portNumber, ', IP address ', listenerProps.ipAddress);
 *    }).catch(error => {
 *      console.error('Failed starting HTTP server: ', error);
 *    });
 *
 * To stop:
 *
 *    httpServer.stop();
 *
 */

export type RequestHandler = (req: Request) => Promise<Response>;

export class HttpListenerService {
  /**
   * HTTP listener service constructor
   *
   * @param {object} options
   * @param {number} options.portNumber - TCP port number to listen on, 0 or <null> = random port
   * @param {string} options.ipAddress - IP address to listen on, '127.0.0.1' (localhost) or '0.0.0.0' (any)
   */
  private _portNumber: number | null;
  private _ipAddress: string;
  private routes: Array<{
    method: RegExp;
    pattern: RegExp;
    handler: RequestHandler;
  }>;
  public listenerProps: GenericNetworkServiceInfo | null;

  constructor({ portNumber = 0, ipAddress = '127.0.0.1' }: { portNumber?: number | null; ipAddress?: string } = {}) {
    this._portNumber = portNumber; // null or 0 -> random port
    this._ipAddress = ipAddress; // either '127.0.0.1' (localhost) or '0.0.0.0' (any)

    // HTTPRequestInfo routing
    this.routes = []; // { method: RegExp, pattern: RegExp, handler: RequestHandler }

    // OBS.Live API /GenericNetworkServiceInfo/ data structure
    // this.listenerProps = null; // { id: 'XXX', portNumber: 12345, ipAddress: '127.0.0.1' };
    this.listenerProps = null;

    this.onHostMessageReceivedHandler = this.onHostMessageReceivedHandler.bind(this);
  }

  /////////////////////////////////////////////////////////////////////
  // HTTPRequestInfo Router Setup API
  /////////////////////////////////////////////////////////////////////

  /**
   * HTTP response send response handler.
   *
   * @callback httpResponseSendHandlerCallback
   * @param {string|object} data - HTTP response body
   */

  /**
   * HTTP response redirect response handler.
   *
   * @callback httpResponseRedirectHandlerCallback
   * @param {string} url - HTTP response redirect to URL
   * @param {number} code - HTTP response code (default: 302)
   */

  /**
   * @typedef HTTPRequestInfo
   * @type {object}
   * @property {string} id - HTTP request unique identifier
   * @property {string} method - HTTP request method
   * @property {string} url - HTTP request URL (path)
   * @property {string} body - HTTP request body
   * @property {object} headers - HTTP request headers key-value map
   * @property {object} query - HTTP query string parameters key-value map
   */

  /**
   * @typedef HTTPResponseInfo
   * @type {object}
   * @property {number} statusCode - HTTP response status code (200, 404, 302, etc.)
   * @property {string} statusText - HTTP response status text ('OK', 'Not Found', 'Found', etc.)
   * @property {string|object} body - HTTP response body
   * @property {object} headers - HTTP response headers key-value map
   * @property {httpResponseSendHandlerCallback} send - Send HTTP response data
   * @property {httpResponseRedirectHandlerCallback} redirect - Send HTTP response redirect
   */

  /**
   * HTTP request handler.
   *
   * @callback httpRequestHandlerCallback
   * @param {HTTPRequestInfo} HTTPRequestInfo
   * @param {HTTPResponseInfo} HTTPResponseInfo
   */

  /**
   * Bind an HTTP request handler to the specified request method and
   * pattern
   *
   * @param {string|RegExp} method - request method regular expression pattern
   * @param {string|RegExp} pattern - request path regular expression pattern
   * @param {httpRequestHandlerCallback} handler - request handler callback
   */
  use(method: string | RegExp, pattern: string | RegExp, handler: RequestHandler) {
    const methodExpr = method instanceof RegExp ? method : new RegExp(method, 'i');
    const patternExpr = pattern instanceof RegExp ? pattern : new RegExp(pattern);

    this.routes.push({ method: methodExpr, pattern: patternExpr, handler });
  }

  /**
   * Bind an HTTP GET request handler to the specified pattern
   *
   * @param {string|RegExp} pattern - request path regular expression pattern
   * @param {httpRequestHandler} handler - request handler callback
   */
  get(pattern: string | RegExp, handler: RequestHandler) {
    this.use('GET', pattern, handler);
  }

  /**
   * Bind an HTTP PUT request handler to the specified pattern
   *
   * @param {string|RegExp} pattern - request path regular expression pattern
   * @param {httpRequestHandler} handler - request handler callback
   */
  put(pattern: string | RegExp, handler: RequestHandler) {
    this.use('PUT', pattern, handler);
  }

  /**
   * Bind an HTTP PATCH request handler to the specified pattern
   *
   * @param {string|RegExp} pattern - request path regular expression pattern
   * @param {httpRequestHandler} handler - request handler callback
   */
  patch(pattern: string | RegExp, handler: RequestHandler) {
    this.use('PATCH', pattern, handler);
  }

  /**
   * Bind an HTTP POST request handler to the specified pattern
   *
   * @param {string|RegExp} pattern - request path regular expression pattern
   * @param {httpRequestHandler} handler - request handler callback
   */
  post(pattern: string | RegExp, handler: RequestHandler) {
    this.use('POST', pattern, handler);
  }

  /**
   * Bind an HTTP DELETE request handler to the specified pattern
   *
   * @param {string|RegExp} pattern - request path regular expression pattern
   * @param {httpRequestHandler} handler - request handler callback
   */
  delete(pattern: string | RegExp, handler: RequestHandler) {
    this.use('DELETE', pattern, handler);
  }

  /**
   * Bind an HTTP OPTIONS request handler to the specified pattern
   *
   * @param {string|RegExp} pattern - request path regular expression pattern
   * @param {httpRequestHandler} handler - request handler callback
   */
  options(pattern: string | RegExp, handler: RequestHandler) {
    this.use('OPTIONS', pattern, handler);
  }

  /////////////////////////////////////////////////////////////////////
  // Start/Stop HTTP listener
  /////////////////////////////////////////////////////////////////////

  /**
   * Check whether the HTTP listener service is active.
   *
   * @returns {boolean}
   */
  get isActive(): boolean {
    return !!this.listenerProps;
  }

  /**
   * Get active HTTP listener port number.
   *
   * @returns {number}
   */
  get portNumber(): number {
    console.log('get portNumber', this.listenerProps);
    return this.listenerProps?.portNumber ?? 0;
  }

  /**
   * Get active HTTP listener IP address.
   *
   * @returns {string}
   */
  get ipAddress(): string {
    return this.listenerProps?.ipAddress ?? '';
  }

  /**
   * Start the HTTP listener service.
   * @returns {Promise<GenericNetworkServiceInfo>} - HTTP listener service properties
   */
  listen = async (): Promise<GenericNetworkServiceInfo> => {
    await ensureOBSPresence();

    if (this.isActive) {
      throw new Error('HttpListenerService.start(): HTTP listener is already active');
    }

    this.listenerProps = await promisefy(window.host.addBrowserScopedHttpServer)({
      portNumber: this._portNumber ?? (null as never),
      ipAddress: this._ipAddress,
    });

    console.log('listenerProps', {
      listenerProps: this.listenerProps,
    });

    if (!this.listenerProps) {
      throw new TransientError('HttpListenerService.start(): window.host.addBrowserScopedHttpServer call failed');
    }

    window.addEventListener('hostMessageReceived', this.onHostMessageReceivedHandler);

    return this.listenerProps;
  };

  /**
   * Stop the HTTP listener service.
   *
   * @returns {Promise}
   */
  async stop(): Promise<void> {
    await ensureOBSPresence();

    if (!this.isActive) {
      throw new Error('HttpListenerService.stop(): HTTP listener is inactive');
    }

    ///
    // First of all, get rid of the message event handler binding.
    //
    // Even if the API call to stop HTTP listener below fails for some reason,
    // we are no longer interested in receiving HTTP requests.
    //
    window.removeEventListener('hostMessageReceived', this.onHostMessageReceivedHandler);

    ///
    // Stop HTTP listener service
    //
    if (this.listenerProps?.id) {
      await promisefy(window.host.removeBrowserScopedHttpServersByIds)([this.listenerProps.id]);
    }
  }

  /////////////////////////////////////////////////////////////////////
  // HTTP request handler
  /////////////////////////////////////////////////////////////////////

  onHostMessageReceivedHandler = async (event: Event) => {
    const packet = (event as CustomEvent).detail;

    // Make sure that the packet source is an HTTP listener
    if (packet.source !== 'http') return;

    console.log('onHostMessageReceivedHandler', packet);

    ///
    // HTTP request
    //
    // OBS.Live API /HTTPRequestInfo/ structure
    //
    const req = packet.message as HTTPRequestInfo;

    const { id, method, url, headers } = req;

    // Make sure that the packet listening port matches ours
    const localHost = headers.Host;

    if (!localHost) return;

    const localPort = Number.parseInt(localHost.split(':')[1]);

    if (localPort !== this.portNumber) {
      console.log('xd');
      return;
    }

    ///
    // HTTP response
    //
    // OBS.Live API /HTTPResponseInfo/ structure

    let response: Response = new Response(null, {
      status: 404,
      statusText: 'Not Found',
    });

    // Scan through route handlers until the request is handled, or,
    // no more routes are available.
    //
    // The first route which handles the request wins.
    //
    for (const route of this.routes) {
      if (!route.method.test(method)) {
        // HTTPRequestInfo HTTP method mismatch
        continue;
      }

      if (!route.pattern.test(url)) {
        // HTTPRequestInfo HTTP URL mismatch
        continue;
      }

      const request = new Request(url, {
        method: method as 'GET' | 'POST',
        headers,
        body: method === 'POST' ? req.body : undefined,
      });

      response = await route.handler(request);

      if (response.status >= 200 && response.status <= 399) {
        // We're done: the request was properly handled
        break;
      }
    }

    const res = {
      headers: Object.fromEntries(response.headers.entries()),
      statusCode: response.status,
      statusText: response.statusText,
      success: response.status >= 200 && response.status <= 399,
      send: (data: unknown) => {
        res.body = JSON.stringify(data);
      },
      redirect: (url, code = 302) => {
        res.statusCode = code;
        const codeMap: Record<number, string> = {
          301: 'Moved Permanently',
          302: 'Found',
          303: 'See Other',
          304: 'Not Modified',
          307: 'Temporary Redirect',
          308: 'Permanent Redirect',
        };
        res.statusText = codeMap[code] || 'Redirect';
        res.headers.Location = url;
      },
    } as HTTPResponseInfo;

    ///
    // Send available HTTP response to the client
    //
    window.host.sendHttpRequestResponse(id!, res, (result) => {
      if (!result) {
        console.warn(
          'HttpListenerService.onHostMessageReceivedHandler(): window.host.sendHttpRequestResponse call failed: ',
          id,
          req,
          res
        );
      }
    });
  };
}
